import { Form, FormSubmitHandler } from 'components/Form';
import styles from './Tablet.module.scss';

import { Redirect, Router } from '@reach/router';
import React, { FC, MouseEventHandler, useEffect } from 'react';
import { RouteFC } from 'util/routeTypes';
import { ContactInfoForm } from '../../../components/ContactInfoForm';
import { ReviewAndPayment } from '../ReviewAndPayment/ReviewAndPayment';
import { CHECKOUT_TEST_ID } from '../const';
import { useSubmitOrder } from '../context/SubmitOrder/SubmitOrder';
import { PageLayout } from 'components/PageLayout';
import { TabletPlaceOrderButton } from '../PlaceOrderButton/PlaceOrderButton';
import { CheckoutHeader } from '../CheckoutHeader';
import { EditGiftCardModal } from 'routes/GiftCard/Modal';
import { EditMenuItemModal } from 'routes/MenuItemModal/EditMenuItemModal';
import { MerchantTitle } from 'components/MerchantTitle';
import { DeliveryAddressModal } from 'routes/DeliveryAddressModal/DeliveryAddressModal';
import { DeliveryModalError } from 'routes/DeliveryAddressModal/components/DeliveryModalError';
import { CheckoutAuthentication } from 'routes/Checkout/Authentication/CheckoutAuthentication';
import { useServiceType } from 'hooks/context/serviceType';
import { useSmartNavigate } from 'hooks/useSmartNavigate';
import { useGetLatestAddress } from 'hooks/useApi/useAddress';
import { useDeliveryValidation } from 'hooks/useDeliveryValidation';
import { useCustomer } from 'hooks/context/customer/customer';
import { useOrderTotals } from '../context/useOrderTotals';

export const Checkout: RouteFC = () => {
  const { submit } = useSubmitOrder();

  const navigate = useSmartNavigate();
  const { isLoggedIn } = useCustomer();
  const { asyncValidateAndSetDefaultAddress } = useDeliveryValidation();
  const { hasDeliveryAddress, currentServiceType, isDeliveryChosen } = useServiceType();
  const serverDefaultAddress = useGetLatestAddress();
  const defaultAddress = serverDefaultAddress?.data;
  const { totalWithTip } = useOrderTotals();

  useEffect(() => {
    if (isDeliveryChosen) {
      if (!defaultAddress && hasDeliveryAddress) {
        navigate('~checkout/address');
      } else if (defaultAddress && hasDeliveryAddress && isLoggedIn) {
        asyncValidateAndSetDefaultAddress(defaultAddress, {}, currentServiceType);
      } else if (hasDeliveryAddress && !isLoggedIn) {
        navigate('~checkout/address');
      }
    }
  }, [isDeliveryChosen, defaultAddress]); // eslint-disable-line react-hooks/exhaustive-deps

  const onSubmit: FormSubmitHandler = (e, isValid) => {
    if (isValid) {
      submit(totalWithTip);
    }
  };

  return (
    <>
      <Router primary={false}>
        <EditGiftCardModal path="gift-card/:index" />
        <EditMenuItemModal path="item/:cartId" />
        <DeliveryModalError path="address-error" />
        <DeliveryAddressModal path="address" />
        <CheckoutAuthentication path="login/*" />
      </Router>
      <Form
        className={styles.wrapper}
        onSubmit={onSubmit}
        data-testid={CHECKOUT_TEST_ID.form}
      >
        <ContactInfoForm />
        <ReviewAndPayment />
      </Form>
    </>
  );
};

const TabletHeader: FC = () => {
  const { submit } = useSubmitOrder();
  const { totalWithTip } = useOrderTotals();
  const onSubmit: MouseEventHandler = () => {
      submit(totalWithTip);
    };

  return (
    <CheckoutHeader className={styles.header}>
      <TabletPlaceOrderButton onClick={onSubmit} />
    </CheckoutHeader>
  );
};

export const Tablet: React.FC = () => (
  <PageLayout
  header={<TabletHeader />}
  customMainClass={styles.checkoutMain}
>
  <MerchantTitle i18nKey="helmet.titleCheckout" />
    <Router className={styles.routerWrapper}>
      <Redirect default from="/*" to="../" noThrow />
      <Checkout path="/*" />
    </Router>
  </PageLayout>
);
